import { ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline'

interface PaginationProps {
  currentPage: number
  lastPage: number
  total?: number
  perPage?: number
  onPageChange: (page: number) => void
  onPerPageChange?: (perPage: number) => void
  disabled?: boolean
  className?: string
  perPageOptions?: number[]
}

export default function Pagination({
  currentPage,
  lastPage,
  total,
  perPage,
  onPageChange,
  onPerPageChange,
  disabled = false,
  className = '',
  perPageOptions = [10, 25, 50, 100]
}: PaginationProps) {
  if (lastPage <= 1 && !total) return null

  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    
    if (lastPage <= 7) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i)
        }
        pages.push('...')
        pages.push(lastPage)
      } else if (currentPage >= lastPage - 3) {
        pages.push(1)
        pages.push('...')
        for (let i = lastPage - 4; i <= lastPage; i++) {
          pages.push(i)
        }
      } else {
        pages.push(1)
        pages.push('...')
        pages.push(currentPage - 1)
        pages.push(currentPage)
        pages.push(currentPage + 1)
        pages.push('...')
        pages.push(lastPage)
      }
    }
    
    return pages
  }

  const startItem = perPage ? (currentPage - 1) * perPage + 1 : 0
  const endItem = perPage ? Math.min(currentPage * perPage, total || 0) : 0

  return (
    <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 ${className}`}>
      <div className="flex items-center gap-4 w-full sm:w-auto text-sm text-neutral-600">
        {onPerPageChange && perPage && (
          <div className="flex items-center gap-2">
            <span>Tampilkan</span>
            <select
              disabled={disabled}
              value={perPage}
              onChange={(e) => {
                onPerPageChange(Number(e.target.value))
              }}
              className="border border-neutral-300 rounded-md text-sm py-1 pl-2 pr-6 focus:ring-primary-500 focus:border-primary-500 bg-white"
            >
              {perPageOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            <span>data</span>
          </div>
        )}
        
        {total !== undefined && perPage !== undefined && (
          <span className="hidden sm:inline">
            Menampilkan {startItem} sampai {endItem} dari {total} data
          </span>
        )}
      </div>

      <div className="flex items-center gap-1 w-full sm:w-auto justify-center sm:justify-end">
        <button
          disabled={currentPage === 1 || disabled}
          onClick={() => onPageChange(1)}
          className="p-2 border border-neutral-300 rounded-lg text-neutral-500 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Ke halaman pertama"
        >
          <ChevronDoubleLeftIcon className="w-4 h-4" />
        </button>
        <button
          disabled={currentPage === 1 || disabled}
          onClick={() => onPageChange(currentPage - 1)}
          className="p-2 border border-neutral-300 rounded-lg text-neutral-500 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Sebelumnya"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        
        <div className="hidden sm:flex items-center gap-1">
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              disabled={page === '...' || disabled}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              className={`px-3 py-1.5 border rounded-lg text-sm font-medium ${
                page === currentPage
                  ? 'border-primary-600 bg-primary-50 text-primary-600'
                  : page === '...'
                  ? 'border-transparent text-neutral-500 bg-transparent cursor-default'
                  : 'border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {page}
            </button>
          ))}
        </div>
        
        <span className="sm:hidden text-sm font-medium text-neutral-700 px-2">
          {currentPage} / {lastPage}
        </span>

        <button
          disabled={currentPage === lastPage || disabled}
          onClick={() => onPageChange(currentPage + 1)}
          className="p-2 border border-neutral-300 rounded-lg text-neutral-500 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Selanjutnya"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
        <button
          disabled={currentPage === lastPage || disabled}
          onClick={() => onPageChange(lastPage)}
          className="p-2 border border-neutral-300 rounded-lg text-neutral-500 bg-white hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Ke halaman terakhir"
        >
          <ChevronDoubleRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}
